import React, { useState, useEffect } from 'react';
import { useStore, useSelector, useDispatch } from 'react-redux';

import Table from '@components/table';
import { prepareTable } from '@components/dataTables/helpers/dataProcess';
import { selectSelectedDatasetOptions } from '@awp/stores/dataTablePageSlice';
import {
  addSelectedObservationId,
  removeSelectedObservationId,
  clearAllSelectedObservationIds,
  selectRefreshCount,
  refreshValidation,
} from '@stores/manualDataValidationSlice';
import {
  selectMultiStationData,
  getSelectedDatasetsByUserPreset,
  derivedObservatioIds,
} from '@stores/dataVisualizationSlice';

import { clearSelection } from '@components/ManualDataValidation/helpers';
import { selectAllUserPresets } from '@stores/userPresetSlice';

const selectedColor = 'rgb(193, 205, 218)';

const getTargetCell = (e, stopPropagation = true, preventDefault = true) => {
  if (preventDefault) e.preventDefault();
  if (stopPropagation) e.stopPropagation();

  let target = e.target;
  while (target.tagName !== 'TD' && target.tagName !== 'TR' && target.parentNode) {
    target = target.parentNode;
  }

  return target.tagName === 'TD' ? target : null;
};

const isItNotApplicableText = (text = '') => {
  text = text.toLowerCase().trim();
  return text.length === 0 || text.includes('missing');
};

let initialObvId = null;

const TableView = () => {
  const store = useStore();
  const dispatch = useDispatch();
  const data = useSelector(selectMultiStationData)?.DataVisualizationDTOs[0];
  const selectedDatasetOptions = useSelector(selectSelectedDatasetOptions);
  const selectedDerivedObservatioIds = useSelector(derivedObservatioIds);
  const filteredSelectedDatasets =
    selectedDerivedObservatioIds != null &&
    selectedDerivedObservatioIds.length > 0 &&
    selectedDatasetOptions != null &&
    selectedDatasetOptions.length > 0 != null
      ? selectedDatasetOptions.filter((x) => !selectedDerivedObservatioIds.includes(x.typeId))
      : selectedDatasetOptions;
  const selectedUserPresets = useSelector(selectAllUserPresets);
  const selectedDatasets = getSelectedDatasetsByUserPreset(filteredSelectedDatasets, data, selectedUserPresets);
  const refreshCount = useSelector(selectRefreshCount);
  const [pageIndex, setPageIndex] = useState(0);
  const [sort, setSort] = useState([{ id: 'Timestamp', desc: true }]);
  const [defaultPageSize, setDefaultPageSize] = useState(30);
  const [maxHeight, setMaxHeight] = useState(0);
  const [startCell, setStartCell] = useState(null);

  useEffect(() => {
    dispatch(clearAllSelectedObservationIds());
    dispatch(refreshValidation());
  }, [selectedDatasets?.length]);

  useEffect(() => {
    dispatch(clearAllSelectedObservationIds());

    const calculateMaxHeight = () => {
      const height = Math.round(window.innerHeight * 0.8);
      setMaxHeight(height);
    };

    calculateMaxHeight();

    const handleMouseDown = (event) => {
      //check if a mousedown is from scroll bars
      if (
        event &&
        event.target &&
        event.target.tagName === 'DIV' &&
        event.target.className.includes('simplebar-wrapper')
      ) {
        event.preventDefault();
        event.stopPropagation();
      } else {
        event.stopPropagation();
      }
    };

    const handleMouseUp = (event) => {
      event.stopPropagation();
    };

    window.addEventListener('resize', calculateMaxHeight);

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    document.addEventListener('touchstart', handleMouseDown);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      dispatch(clearAllSelectedObservationIds());

      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);

      document.removeEventListener('touchstart', handleMouseDown);
      document.removeEventListener('touchend', handleMouseUp);

      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, []);

  const { tableData, tableColumns } = prepareTable(
    selectedDatasets,
    {
      showInvalidData: true,
      showConfidenceLevel: true,
      showValidData: true,
    },
    'manual-data-validation',
  );

  if (!selectedDatasets || selectedDatasets.length === 0) return <></>;

  // get ids directly from store instead of watching, to avoid component re-rendering
  const selectedObservationIds = store.getState().manualDataValidation?.selectedObservationIds ?? [];

  const toggleCell = (elem, obsId) => {
    if (!elem || !obsId) return;

    if (elem.style.background === selectedColor) {
      elem.style.background = '';
      dispatch(removeSelectedObservationId(obsId));
    } else {
      elem.style.background = selectedColor;
      dispatch(addSelectedObservationId(obsId));
    }
  };

  const setCell = (elem, obsId, select) => {
    if (!elem || !obsId) return;

    if (!select) {
      elem.style.background = '';
      dispatch(removeSelectedObservationId(obsId));
    } else {
      elem.style.background = selectedColor;
      dispatch(addSelectedObservationId(obsId));
    }
  };

  return (
    <>
      <Table
        columns={tableColumns}
        data={tableData}
        fetchData={() => {}}
        loading={false}
        isFirstColumnSticky={true}
        key={`tbl-${refreshCount}`}
        index={pageIndex}
        setIndex={setPageIndex}
        sort={sort}
        setSort={setSort}
        defaultPageSize={defaultPageSize}
        setDefaultPageSize={setDefaultPageSize}
        tableParentClasses="obsTable obsTable-table dataTables table-xs"
        tableClasses="manual-data-validation-table"
        maxHeight={maxHeight}
        autoHide={true}
        maxPageSize={200}
        paginationBarClasses="obsTable pagination"
        // Sets the initial background colors
        onCellStyle={(cell) => {
          const obsId = cell.value?.ObservationId;
          if (selectedObservationIds.includes(obsId)) {
            return { background: selectedColor };
          }

          return { background: '' };
        }}
        mouseDownHandler={(e, page, cell, cells, i, j) => {
          const target = getTargetCell(e, false, false);

          if (cell.value?.ObservationId) {
            initialObvId = cell.value?.ObservationId;
          }

          if (target && e.ctrlKey) {
            const selectedItems = store.getState().manualDataValidation?.selectedObservationIds ?? [];
            const isItAllSelected = cells.every((cell) => {
              const obsId = cell.value?.ObservationId;
              if (!obsId) return true;
              return selectedItems.includes(obsId);
            });

            const childElements = target.parentElement.querySelectorAll('td');

            if (isItAllSelected) {
              childElements.forEach((child) => {
                if (isItNotApplicableText(child.textContent)) return;
                child.style.background = '';
              });

              cells.forEach((cell) => {
                const obsId = cell.value?.ObservationId;
                if (!obsId) return;
                dispatch(removeSelectedObservationId(obsId));
              });
            } else {
              childElements.forEach((child) => {
                if (isItNotApplicableText(child.textContent)) return;
                child.style.background = selectedColor;
              });

              cells.forEach((cell) => {
                const obsId = cell.value?.ObservationId;
                if (!obsId) return;
                dispatch(addSelectedObservationId(obsId));
              });
            }
          } else if (target && e.shiftKey) {
            if (startCell) {
              const [startRow, endRow] = startCell.i <= i ? [startCell.i, i] : [i, startCell.i];
              const [startCol, endCol] = startCell.j < j ? [startCell.j, j] : [j, startCell.j];

              for (let m = startRow; m <= endRow; m++) {
                for (let n = startCol; n <= endCol; n++) {
                  const cellId = `${m}-${n}`;
                  const cellElem = document.getElementById(cellId);
                  const obsId = page[m].cells[n].value?.ObservationId;

                  setCell(cellElem, obsId, startCell.selected);
                }
              }
            }
          } else {
            const selectedItems = store.getState().manualDataValidation?.selectedObservationIds ?? [];
            const obsId = cell.value?.ObservationId;
            if (!obsId) {
              setStartCell(null);
              return;
            }
            setStartCell({ i, j, selected: !selectedItems.includes(obsId) });
          }
        }}
        mouseUpHandler={(e, cell) => {
          clearSelection();

          if (e.ctrlKey || e.shiftKey) {
            return;
          }

          const target = getTargetCell(e);
          if (!target || !target.style) return;
          const obsId = cell.value?.ObservationId;
          if (!obsId) return;

          // Toggle on the same cell only
          if (initialObvId !== obsId) {
            return;
          }

          toggleCell(target, obsId);
        }}
      />
    </>
  );
};

export default TableView;
